"use client";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ConvertLink } from "@/util/tools";
import { PersianNum } from "@/util/tools/PersianNum";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useMemo } from "react";
import AttributePrices from "./components/AttributePrices";
import CommentList from "./components/CommentList";
import GalleryProduct from "./components/GalleryProduct";
import MainAttribute from "./components/MainAttribute";
import RateList from "./components/RateList";
import SellButton from "./components/SellButton";
import { CommentsType, ParseUrl, ProductByURLType } from "./product";
import { loadData } from "./tools";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import GalleryImages from "./components/gallery/GalleryImages";

const FavoriteButton = dynamic(() => import("./components/FavoriteButton"), {
  ssr: false,
});

interface Props {
  product: ProductByURLType;
  comments: CommentsType;
  url: ParseUrl[];
  token: any;
}

const ProductView = async ({ comments, product, url, token }: Props) => {
  const MainAttributes = useMemo(() => {
    const property = product.ProdProperties.filter(
      (b) => b.Property.property_main === true
    ).map((i) => {
      return {
        name: i.Property.property_name,
        value: i.Attribute.attribute_name,
      };
    });
    const result = Object.values(
      property.reduce((acc: any, curr) => {
        if (acc[curr.name]) {
          acc[curr.name].value += ` , ${curr.value}`;
        } else {
          acc[curr.name] = { ...curr };
        }
        return acc;
      }, {})
    );
    return result;
  }, [product.ProdProperties]);

  const NormalAttribute = useMemo(() => {
    const property: { name: string; value: string }[] =
      product.ProdProperties.map((i) => {
        return {
          name: i.Property.property_name,
          value: i.Attribute.attribute_name,
        };
      });
    const result = Object.values(
      property.reduce((acc: any, curr) => {
        if (acc[curr.name]) {
          acc[curr.name].value += ` , ${curr.value}`;
        } else {
          acc[curr.name] = { ...curr };
        }
        return acc;
      }, {})
    );
    return result;
  }, [product.ProdProperties]);

  const DiscountShow = ({
    price,
    discount,
  }: {
    price: number;
    discount: number;
  }) => (
    <div className="flex items-center">
      <PriceShow prices={discount} />
      <div className="flex items-center line-through mx-10 text-slate-500">
        <p>{PersianNum(price)}</p>
        <span>تومان</span>
      </div>
    </div>
  );

  const PriceShow = ({ prices }: { prices: number }) => (
    <div className="flex items-center text-red-600 font-bold">
      <p>{PersianNum(prices)}</p>
      <span>تومان</span>
    </div>
  );

  return (
    <div>
      <div className="relative">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <Link href="/">خانه</Link>
            </BreadcrumbItem>
            <span>/</span>
            <BreadcrumbItem>
              <Link
                href={`/categories/${ConvertLink(product.Category.cat_url)}`}
              >
                {product.Category.cat_title}
              </Link>
            </BreadcrumbItem>
            <span>/</span>
            <BreadcrumbItem>
              <BreadcrumbPage>{product.product_title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <Card className="dark:bg-slate-600">
        <div className="flex items-start justify-around flex-wrap">
          <div className="p-3 w-full sm:w-[50%]">
            <GalleryImages data={product} />
          </div>
          <div className="w-full p-3 sm:w-[50%]">
            <div className="flex flex-wrap justify-between">
              <div>
                <h1 className="text-xl"> {product.product_title}</h1>
              </div>
              <FavoriteButton token={token} prodId={product.id} />
            </div>
            <div className="my-2 flex">
              <div className="flex items-center">
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  دسته بندی :
                </p>
                <Link
                  className="text-blue-900 dark:text-slate-200 text-base mx-3"
                  href={`/categories/${ConvertLink(
                    product.Category.cat_url
                  )}?page=1`}
                >
                  {product.Category.cat_title}
                </Link>
              </div>
              <div className="flex items-center">
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  برند :
                </p>
                <Link
                  className="text-blue-900 dark:text-slate-200 text-base mx-3"
                  href={`/brand/${ConvertLink(product.Brand.brand_url)}?page=1`}
                >
                  {product.Brand.brand_title}
                </Link>
              </div>
            </div>
            <div className="inline-block">
              <MainAttribute data={MainAttributes} />
            </div>
            <div>
              <Dialog>
                <DialogTrigger asChild>
                  <div className="inline-flex p-1 rounded-md bg-white text-stone-800">
                    <span className="ml-2 text-sm">راهنمای سایز </span>
                    <span>
                      <svg
                        fill="#000000"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g id="Ruler">
                          <g>
                            <path d="M9.479,3.5a1.5,1.5,0,0,0-2.12,0L3.5,7.35a1.516,1.516,0,0,0-.44,1.06A1.5,1.5,0,0,0,3.5,9.47L14.519,20.5a1.509,1.509,0,0,0,2.13,0l3.85-3.86a1.491,1.491,0,0,0,0-2.12ZM8.359,7.08a.5.5,0,0,0,0,.71.524.524,0,0,0,.71,0c.55-.56,1.09-1.1,1.65-1.64l1.25,1.25-.9.9a.483.483,0,0,0,0,.7.5.5,0,0,0,.71,0c.29-.3.6-.6.9-.89l1.25,1.25-1.64,1.65a.495.495,0,0,0,.7.7c.56-.55,1.1-1.09,1.65-1.64l1.25,1.25-.9.9a.524.524,0,0,0-.14.36.5.5,0,0,0,.14.35.513.513,0,0,0,.71,0l.9-.9,1.26,1.26-1.65,1.64a.5.5,0,0,0,.71.71c.55-.56,1.09-1.1,1.65-1.64l1.23,1.23a.5.5,0,0,1,0,.7l-3.86,3.86a.5.5,0,0,1-.71,0L4.209,8.77a.491.491,0,0,1-.15-.36.485.485,0,0,1,.15-.35L8.069,4.2a.508.508,0,0,1,.7,0l1.24,1.24Z" />
                            <path d="M18.939,12.96l-.04-.04c.01,0,.01,0,.02.01S18.939,12.95,18.939,12.96Z" />
                          </g>
                        </g>
                      </svg>
                    </span>
                  </div>
                </DialogTrigger>
                <DialogContent>
                  <Image alt="سایز" height={500} width={500} src={process.env.imageUrl + product.product_image_second} />
                </DialogContent>
              </Dialog>
            </div>
            {product.product_type === "simple" ? (
              <div>
                <div className="flex w-[60%] justify-between">
                  <p className="dark:text-slate-700">قیمت کالا :</p>
                  {product.ProductIdentities[0]?.Warehouses[0]
                    .warehouse_discount === 0 ? (
                    <PriceShow
                      prices={
                        product.ProductIdentities[0]?.Warehouses[0]
                          .warehouse_price
                      }
                    />
                  ) : (
                    <DiscountShow
                      discount={
                        product.ProductIdentities[0]?.Warehouses[0]
                          .warehouse_discount
                      }
                      price={
                        product.ProductIdentities[0]?.Warehouses[0]
                          .warehouse_price
                      }
                    />
                  )}
                </div>
                <SellButton
                  ProductId={product.id}
                  ProductIdentityId={product.ProductIdentities[0]?.id}
                  available={
                    product.ProductIdentities[0]?.Warehouses[0]
                      ?.warehouse_available
                  }
                />
              </div>
            ) : (
              <AttributePrices
                data={product.ProductIdentities}
                checkData={loadData(product)}
                url={url}
              />
            )}
            {product.product_text_box && (
              <p className="text-xs p-2">
                توضیحات کوتاه : {product.product_text_box}
              </p>
            )}
          </div>
          <div className="md:w-[20%] p-3 sm:w-full">
            <RateList rates={product.Rates} />
          </div>
        </div>
      </Card>
      <Tabs
        defaultValue="attribute"
        style={{ direction: "rtl" }}
        className="w-full my-3"
      >
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="content">نقد و بررسی</TabsTrigger>
          <TabsTrigger value="attribute">ویژگی ها</TabsTrigger>
          <TabsTrigger value="comments">نظرات</TabsTrigger>
        </TabsList>
        <TabsContent value="content">
          <Card className="dark:bg-slate-600">
            <div
              className="p-5"
              dangerouslySetInnerHTML={{ __html: product.product_description }}
            />
          </Card>
        </TabsContent>
        <TabsContent value="attribute">
          <Card className="p-5 dark:bg-slate-600">
            <MainAttribute data={NormalAttribute} />
          </Card>
        </TabsContent>
        <TabsContent value="comments">
          <Card className="p-5 dark:bg-slate-600">
            <CommentList token={token} prodId={product.id} data={comments} />
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProductView;
